import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/stores/auth"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { Logo } from "@/components/marketing/logo"
import { LanguageSwitcher } from "@/components/marketing/language-switcher"
import { useAppearance, type Appearance } from "@/hooks/use-appearance"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { CloseCircle, HamburgerMenu, Monitor, Moon, PaletteRound, Sun } from "@solar-icons/react"
import { useTranslation } from "react-i18next"

const NAV_ITEMS = [
  { key: "solutions" as const, href: "/solutions" },
  { key: "developers" as const, href: "/developers" },
  { key: "contact" as const, href: "/contact" },
]

export function MarketingHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const token = useAuthStore((state) => state.token)
  const isAuthenticated = !!token
  const navigate = useNavigate()
  const { appearance, updateAppearance } = useAppearance()
  const { t } = useTranslation(["marketing", "settings"])

  const themeOptions: { value: Appearance; icon: typeof Sun; labelKey: "appearance.light" | "appearance.dark" | "appearance.system" }[] = [
    { value: "light", icon: Sun, labelKey: "appearance.light" },
    { value: "dark", icon: Moon, labelKey: "appearance.dark" },
    { value: "system", icon: Monitor, labelKey: "appearance.system" },
  ]

  return (
    <header className="fixed top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container mx-auto flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo size="lg" />

        <div className="hidden md:flex md:items-center md:gap-8">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t(`header.nav.${item.key}`)}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex md:items-center md:gap-2">
          <LanguageSwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                aria-label={t("appearance.theme", { ns: "settings" })}
              >
                <PaletteRound className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {themeOptions.map(({ value, icon: Icon, labelKey }) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => updateAppearance(value)}
                  className={cn(appearance === value && "bg-accent")}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {t(labelKey, { ns: "settings" })}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {isAuthenticated ? (
            <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white" onClick={() => navigate("/dashboard")}>
              {t("header.goToDashboard")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/sign-in")}>
                {t("header.signIn")}
              </Button>
              <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white" onClick={() => navigate("/sign-up")}>
                {t("header.getStarted")}
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="sr-only">{t("header.openMenu")}</span>
          {mobileMenuOpen ? (
            <CloseCircle className="h-6 w-6" />
          ) : (
            <HamburgerMenu className="h-6 w-6" />
          )}
        </button>
      </nav>

      <div
        className={cn(
          "md:hidden border-t",
          mobileMenuOpen ? "block" : "hidden"
        )}
      >
        <div className="space-y-1 px-4 py-4">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className="block rounded-md px-3 py-2 text-base font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t(`header.nav.${item.key}`)}
            </Link>
          ))}
          <div className="flex items-center gap-2 py-2 border-b">
            <LanguageSwitcher variant="full" onSelect={() => setMobileMenuOpen(false)} />
          </div>
          <div className="flex items-center gap-2 py-2 border-b">
            <PaletteRound className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">{t("appearance.theme", { ns: "settings" })}</span>
          </div>
          <div className="flex gap-2 py-2">
            {themeOptions.map(({ value, icon: Icon, labelKey }) => (
              <Button
                key={value}
                variant={appearance === value ? "secondary" : "ghost"}
                size="sm"
                onClick={() => {
                  updateAppearance(value)
                  setMobileMenuOpen(false)
                }}
                className="flex items-center gap-1.5"
              >
                <Icon className="h-4 w-4" />
                {t(labelKey, { ns: "settings" })}
              </Button>
            ))}
          </div>
          <div className="pt-4 space-y-2">
            {isAuthenticated ? (
              <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white" onClick={() => navigate("/dashboard")}>
                {t("header.goToDashboard")}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/sign-in")}
                >
                  {t("header.signIn")}
                </Button>
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white" onClick={() => navigate("/sign-up")}>
                  {t("header.getStarted")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
